import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Card, CardMedia, CardContent, IconButton, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

const FavoritesPaper = styled(Paper)({
  padding: '2rem',
  backgroundColor: '#fafafa',
  border: '1px solid #eee',
});

const SectionTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '1.8rem',
  color: '#1a1a1a',
  marginBottom: '1.5rem',
});

const ProductCard = styled(Card)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  boxShadow: 'none',
  border: '1px solid #eee',
  position: 'relative',
});

const ProductImage = styled(CardMedia)({
  height: 200,
  backgroundSize: 'contain',
  backgroundColor: '#fafafa',
});

const ProductTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '1.2rem',
  marginBottom: '0.5rem',
});

const ProductPrice = styled(Typography)({
  color: '#c4a661',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
});

const CommentBox = styled(Box)({
  marginTop: 'auto',
  padding: '1rem',
  backgroundColor: '#fafafa',
  borderTop: '1px solid #eee',
});

const Favorites = () => {
  // Mock data - replace with real data from backend
  const [favorites, setFavorites] = useState([
    {
      id: 1,
      name: 'Diamond Solitaire Ring',
      price: '$4,999',
      image: '/images/products/ring1.jpg',
      comment: 'This classic solitaire is perfect for any occasion. The brilliance is stunning!',
      isEditing: false,
    },
    {
      id: 2,
      name: 'Pearl Necklace',
      price: '$2,499',
      image: '/images/products/necklace1.jpg',
      comment: 'The elegance of these pearls is timeless. A must-have for your collection.',
      isEditing: false,
    },
    {
      id: 3,
      name: 'Sapphire Earrings',
      price: '$3,499',
      image: '/images/products/earrings1.jpg',
      comment: 'These sapphire earrings add the perfect pop of color to any outfit.',
      isEditing: false,
    },
  ]);

  const handleCommentChange = (id, newComment) => {
    setFavorites(favorites.map(item => 
      item.id === id ? { ...item, comment: newComment } : item
    ));
  };

  const toggleEdit = (id) => {
    setFavorites(favorites.map(item =>
      item.id === id ? { ...item, isEditing: !item.isEditing } : item
    ));
  };

  const handleRemove = (id) => {
    setFavorites(favorites.filter(item => item.id !== id));
  };

  return (
    <FavoritesPaper elevation={0}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <SectionTitle>My Favorite Items</SectionTitle>
        <Typography color="textSecondary">
          {favorites.length} items selected
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {favorites.map((item) => (
          <Grid item key={item.id} xs={12} sm={6} md={4}>
            <ProductCard>
              <ProductImage
                image={item.image}
                title={item.name}
              />
              <CardContent>
                <ProductTitle variant="h6">{item.name}</ProductTitle>
                <ProductPrice>{item.price}</ProductPrice>
                
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(item.id)}
                    sx={{ backgroundColor: 'rgba(255,255,255,0.8)', mr: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>

              <CommentBox>
                {item.isEditing ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={item.comment}
                      onChange={(e) => handleCommentChange(item.id, e.target.value)}
                      placeholder="Add your comment about this item..."
                      size="small"
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleEdit(item.id)}
                        color="primary"
                      >
                        <SaveIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {item.comment}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleEdit(item.id)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </CommentBox>
            </ProductCard>
          </Grid>
        ))}
      </Grid>

      {favorites.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No favorite items selected yet
          </Typography>
          <Typography color="textSecondary">
            Browse our collections and add items to your favorites
          </Typography>
        </Box>
      )}
    </FavoritesPaper>
  );
};

export default Favorites;
